import { api } from "@/lib/api";
import {
  ApiResponse,
  ErrorResponse,
  MaintenanceExecutionEntry,
  MaintenanceTask,
} from "@/lib/types";
import { MutationOptions, useMutation } from "@tanstack/react-query";

export interface CreateMaintenanceExecutionEntry {
  vesselId: string;
  taskId: string;
  executionDate: Date;
  runningHoursAtExecution?: number;
  notes?: string;
}

export const useCreateMaintenanceExecutionEntry = (
  options?: MutationOptions<
    MaintenanceExecutionEntry,
    ErrorResponse,
    CreateMaintenanceExecutionEntry
  >
) => {
  return useMutation<
    MaintenanceExecutionEntry,
    ErrorResponse,
    CreateMaintenanceExecutionEntry
  >({
    mutationKey: ["create-maintenance-execution-entry"],
    mutationFn: async (data: CreateMaintenanceExecutionEntry) => {
      const response = await api.post<ApiResponse<MaintenanceExecutionEntry>>(
        `/vessels/${data?.vesselId}/maintenance/tasks/${data?.taskId}/execution-entries`,
        data
      );
      return response.data.value;
    },
    ...options,
  });
};

export interface UpdateMaintenanceExecutionEntry {
  id: string;
  vesselId: string;
  taskId: string;
  executionDate: Date;
  runningHoursAtExecution?: number;
  notes?: string;
  version: number;
}

export const useUpdateMaintenanceExecutionEntry = (
  options?: MutationOptions<
    MaintenanceExecutionEntry,
    ErrorResponse,
    UpdateMaintenanceExecutionEntry
  >
) => {
  return useMutation<
    MaintenanceExecutionEntry,
    ErrorResponse,
    UpdateMaintenanceExecutionEntry
  >({
    mutationKey: ["update-maintenance-execution-entry"],
    mutationFn: async (data: UpdateMaintenanceExecutionEntry) => {
      const response = await api.put<ApiResponse<MaintenanceExecutionEntry>>(
        `/vessels/${data?.vesselId}/maintenance/tasks/${data?.taskId}/execution-entries/${data?.id}`,
        data
      );
      return response.data.value;
    },
    ...options,
  });
};

export interface DeleteMaintenanceExecutionEntry {
  vesselId: string;
  taskId: string;
  id: string;
}

export const useDeleteMaintenanceExecutionEntry = (
  options?: MutationOptions<
    null,
    ErrorResponse,
    DeleteMaintenanceExecutionEntry
  >
) => {
  return useMutation<null, ErrorResponse, DeleteMaintenanceExecutionEntry>({
    mutationKey: ["delete-maintenance-execution-entry"],
    mutationFn: async (data: DeleteMaintenanceExecutionEntry) => {
      await api.delete<ApiResponse<null>>(
        `/vessels/${data?.vesselId}/maintenance/tasks/${data?.taskId}/execution-entries/${data?.id}`
      );
      return null;
    },
    ...options,
  });
};

export interface CreateMaintenanceExecutionEntryAttachmentRequest {
  vesselId: string;
  taskId: string;
  executionEntryId: string;
  file: File;
  description?: string;
}

export const useCreateMaintenanceExecutionEntryAttachment = (
  options?: MutationOptions<
    MaintenanceExecutionEntry,
    ErrorResponse,
    CreateMaintenanceExecutionEntryAttachmentRequest
  >
) => {
  return useMutation<
    MaintenanceExecutionEntry,
    ErrorResponse,
    CreateMaintenanceExecutionEntryAttachmentRequest
  >({
    mutationKey: ["create-maintenance-execution-entry-attachment"],
    mutationFn: async (
      data: CreateMaintenanceExecutionEntryAttachmentRequest
    ) => {
      const formData = new FormData();
      formData.append("file", data.file);

      if (data.description) {
        formData.append("description", data.description);
      }

      const response = await api.post<MaintenanceExecutionEntry>(
        `/vessels/${data?.vesselId}/maintenance/tasks/${data?.taskId}/execution-entries/${data?.executionEntryId}/attachments`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      return response.data;
    },
    ...options,
  });
};

// --- Task Assignment Mutations ---

export interface AssignTaskRequest {
  vesselId: string;
  taskId: string;
  userId: string;
  version: number;
  detailVersion: number;
}

export const useAssignTask = (
  options?: MutationOptions<MaintenanceTask, ErrorResponse, AssignTaskRequest>
) => {
  return useMutation<MaintenanceTask, ErrorResponse, AssignTaskRequest>({
    mutationKey: ["assign-task"],
    mutationFn: async (data: AssignTaskRequest) => {
      const response = await api.put<ApiResponse<MaintenanceTask>>(
        `/vessels/${data.vesselId}/maintenance/tasks/${data.taskId}/assignment`,
        {
          userId: data.userId,
          version: data.version,
          detailVersion: data.detailVersion,
        }
      );
      return response.data.value;
    },
    ...options,
  });
};

export interface UnassignTaskRequest {
  vesselId: string;
  taskId: string;
  userId: string;
  version: number;
  detailVersion: number;
}

export const useUnassignTask = (
  options?: MutationOptions<null, ErrorResponse, UnassignTaskRequest>
) => {
  return useMutation<null, ErrorResponse, UnassignTaskRequest>({
    mutationKey: ["unassign-task"],
    mutationFn: async (data: UnassignTaskRequest) => {
      await api.delete(
        `/vessels/${data.vesselId}/maintenance/tasks/${data.taskId}/assignment`,
        {
          params: {
            userId: data.userId,
            version: data.version,
            detailVersion: data.detailVersion,
          },
        }
      );
      return null;
    },
    ...options,
  });
};

export interface DeleteMaintenanceExecutionEntryAttachmentRequest {
  vesselId: string;
  taskId: string;
  executionEntryId: string;
  attachmentId: string;
}

export const useDeleteMaintenanceExecutionEntryAttachment = (
  options?: MutationOptions<
    null,
    ErrorResponse,
    DeleteMaintenanceExecutionEntryAttachmentRequest
  >
) => {
  return useMutation<
    null,
    ErrorResponse,
    DeleteMaintenanceExecutionEntryAttachmentRequest
  >({
    mutationKey: ["delete-maintenance-execution-entry-attachment"],
    mutationFn: async (
      data: DeleteMaintenanceExecutionEntryAttachmentRequest
    ) => {
      const response = await api.delete<null>(
        `/vessels/${data?.vesselId}/maintenance/tasks/${data?.taskId}/execution-entries/${data?.executionEntryId}/attachments/${data?.attachmentId}`
      );
      return response.data;
    },
    ...options,
  });
};
